import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  Table,
} from "reactstrap";

import { confirmAdminAction, safeHttpUrl } from "admin/contentUtils";
import AdminListControls from "components/admin/AdminListControls";
import AdminPageHeader from "components/admin/AdminPageHeader";
import AdminStateBlock from "components/admin/AdminStateBlock";
import StatCard from "components/StatCard";
import { applicationsRepo } from "data/repositories/applicationsRepo";
import { provincesRepo } from "data/repositories/provincesRepo";

const STATUS_LABEL = {
  pending: "Bekliyor",
  approved: "Onaylandı",
  rejected: "Reddedildi",
};

const STATUS_COLOR = {
  pending: "warning",
  approved: "success",
  rejected: "danger",
};

const CATEGORY_LABEL = {
  hotel: "Otel",
  resort: "Tatil köyü",
  pension: "Pansiyon",
  restaurant: "Restoran",
  souvenir: "Hediyelik",
  transport: "Ulaşım",
  agency: "Acente",
  guide: "Rehber",
};

const TABS = [
  { key: "business", label: "İşletme başvuruları" },
  { key: "deal", label: "Fırsat başvuruları" },
  { key: "ad", label: "Reklam başvuruları" },
];

const PLACEMENT_LABEL = {
  all: "Tüm sayfalar",
  home: "Ana sayfa",
  map: "Harita",
  deals: "Fırsatlar",
  businesses: "İşletmeler",
};

const REVIEW_QUESTION = {
  business: "başvurusu onaylansın mı? Onay sonrası işletme kaydı oluşturulur.",
  deal: "fırsatı onaylansın mı? Onay sonrası Fırsatlar sayfasında yayınlanır.",
  ad: "reklamı onaylansın mı? Onay sonrası uygulamalarda yayınlanır.",
};

const APPROVED_MESSAGE = {
  business: "; işletme kaydı oluşturuldu.",
  deal: " ve yayına alındı.",
  ad: " ve reklam yayına alındı.",
};

const APPROVE_LABEL = {
  business: "Onayla ve işletme oluştur",
  deal: "Onayla ve yayınla",
  ad: "Onayla ve yayınla",
};

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("tr-TR");
}

function formatDay(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return value || "—";
  const [year, month, day] = value.split("-");
  return `${day}.${month}.${year}`;
}

function DetailRow({ label, children }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children || "—"}</dd>
    </>
  );
}

export default function AdminApplications() {
  const [tab, setTab] = useState("business");
  const [businessApps, setBusinessApps] = useState([]);
  const [dealApps, setDealApps] = useState([]);
  const [adApps, setAdApps] = useState([]);
  const [loadErrors, setLoadErrors] = useState({ business: "", deal: "", ad: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");
  const [busyId, setBusyId] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [selectedId, setSelectedId] = useState("");
  const [note, setNote] = useState("");

  const provinceNameById = useMemo(() => {
    const map = new Map();
    for (const province of provincesRepo.list()) map.set(province.id, province.name);
    return map;
  }, []);
  const provinceName = (id) => provinceNameById.get(id) || id || "—";

  const load = useCallback(async () => {
    setLoading(true);
    const [businessResult, dealResult, adResult] = await Promise.allSettled([
      applicationsRepo.listBusinessApplications(),
      applicationsRepo.listDealApplications(),
      applicationsRepo.listAdApplications(),
    ]);
    const errorOf = (result) => (result.status === "rejected" ? result.reason?.message || "Yüklenemedi." : "");
    setBusinessApps(businessResult.status === "fulfilled" ? businessResult.value : []);
    setDealApps(dealResult.status === "fulfilled" ? dealResult.value : []);
    setAdApps(adResult.status === "fulfilled" ? adResult.value : []);
    setLoadErrors({ business: errorOf(businessResult), deal: errorOf(dealResult), ad: errorOf(adResult) });
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const itemsByTab = { business: businessApps, deal: dealApps, ad: adApps };
  const items = itemsByTab[tab];
  const countPending = (list) => list.filter((item) => item.status === "pending").length;
  const pendingByTab = { business: countPending(businessApps), deal: countPending(dealApps), ad: countPending(adApps) };
  const approvedTotal = [...businessApps, ...dealApps, ...adApps].filter((item) => item.status === "approved").length;

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items.filter((item) => {
      if (statusFilter && item.status !== statusFilter) return false;
      if (!query) return true;
      return [item.businessName, item.title, item.contactName, item.phone, item.email, item.district]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [items, search, statusFilter]);

  const selected = filtered.find((item) => item.id === selectedId) || filtered[0] || null;

  useEffect(() => {
    if (selected && selected.id !== selectedId) {
      setSelectedId(selected.id);
      setNote("");
    }
    if (!selected && selectedId) {
      setSelectedId("");
      setNote("");
    }
  }, [selected, selectedId]);

  const changeTab = (nextTab) => {
    setTab(nextTab);
    setSearch("");
    setStatusFilter("pending");
    setSelectedId("");
    setNote("");
    setFlash("");
    setError("");
  };

  const displayName = (item) => (item.kind === "business" ? item.businessName : item.title || item.businessName);

  const review = async (item, status) => {
    const name = displayName(item);
    const question = status === "approved" ? REVIEW_QUESTION[item.kind] : "başvurusu reddedilsin mi?";
    if (!confirmAdminAction(name, question)) return;

    setBusyId(item.id);
    setError("");
    setFlash("");
    try {
      if (item.kind === "deal") await applicationsRepo.reviewDeal(item.id, status, note);
      else if (item.kind === "ad") await applicationsRepo.reviewAd(item.id, status, note);
      else await applicationsRepo.reviewBusiness(item.id, status, note);
      setFlash(status === "approved" ? `${name} onaylandı${APPROVED_MESSAGE[item.kind]}` : `${name} reddedildi.`);
      setNote("");
      await load();
    } catch (reviewError) {
      setError(reviewError?.message || "Başvuru güncellenemedi.");
    } finally {
      setBusyId("");
    }
  };

  const filters = [
    {
      key: "status",
      label: "Durum",
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        { value: "", label: "Tüm durumlar" },
        { value: "pending", label: "Bekleyen" },
        { value: "approved", label: "Onaylanan" },
        { value: "rejected", label: "Reddedilen" },
      ],
    },
  ];

  const tabLoadError = loadErrors[tab];

  return (
    <>
      <AdminPageHeader
        title="Onay Bekleyenler"
        description="Portal ve mobil uygulamadan gelen işletme, fırsat ve reklam başvurularını inceleyin, onaylayın veya reddedin."
        primaryAction={load}
        primaryLabel="Yenile"
      />
      <Container className="mt--6" fluid>
        <Row className="mb-4">
          <Col lg={3} md={6} className="mb-3">
            <StatCard title="Bekleyen işletme" value={pendingByTab.business} icon="fas fa-store" color="warning" />
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <StatCard title="Bekleyen fırsat" value={pendingByTab.deal} icon="fas fa-tags" color="info" />
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <StatCard title="Bekleyen reklam" value={pendingByTab.ad} icon="fas fa-bullhorn" color="primary" />
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <StatCard title="Onaylanan" value={approvedTotal} icon="fas fa-check" color="success" />
          </Col>
        </Row>

        <Nav tabs className="mb-3">
          {TABS.map((item) => {
            const count = pendingByTab[item.key];
            return (
              <NavItem key={item.key}>
                <NavLink
                  href="#"
                  className={tab === item.key ? "active" : ""}
                  onClick={(event) => {
                    event.preventDefault();
                    changeTab(item.key);
                  }}
                >
                  {item.label}
                  {count ? (
                    <Badge color="warning" pill className="ml-2">
                      {count}
                    </Badge>
                  ) : null}
                </NavLink>
              </NavItem>
            );
          })}
        </Nav>

        {tabLoadError ? (
          <Alert color="danger" className="mb-4">
            {tabLoadError}
          </Alert>
        ) : null}

        <Row>
          <Col xl="7" className="mb-4">
            <Card className="shadow">
              <CardHeader>
                <h3 className="mb-3">{TABS.find((item) => item.key === tab)?.label}</h3>
                <AdminListControls
                  search={search}
                  onSearch={setSearch}
                  searchPlaceholder="İşletme, başlık, yetkili, telefon veya e-posta ara"
                  filters={filters}
                  sortValue="created-desc"
                  onSortChange={() => {}}
                  sortOptions={[{ value: "created-desc", label: "En yeni" }]}
                  onReset={() => {
                    setSearch("");
                    setStatusFilter("pending");
                  }}
                  resultCount={filtered.length}
                  totalCount={items.length}
                />
              </CardHeader>
              <CardBody>
                <AdminStateBlock
                  flash={flash ? { color: "success", message: flash } : null}
                  onDismissFlash={() => setFlash("")}
                  error={error}
                  onDismissError={() => setError("")}
                  loading={loading}
                  isEmpty={!filtered.length}
                  emptyTitle="Başvuru yok"
                  emptyDescription={statusFilter === "pending" ? "Onay bekleyen başvuru bulunmuyor." : "Filtrelere uyan başvuru yok."}
                >
                  <Table responsive hover className="align-items-center mb-0">
                    <thead className="thead-light">
                      <tr>
                        <th>{{ business: "İşletme", deal: "Fırsat", ad: "Reklam" }[tab]}</th>
                        <th>Şehir</th>
                        <th>Yetkili</th>
                        <th>Durum</th>
                        <th>Tarih</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((item) => (
                        <tr
                          key={item.id}
                          onClick={() => {
                            setSelectedId(item.id);
                            setNote("");
                          }}
                          style={{ cursor: "pointer", background: selected?.id === item.id ? "rgba(94, 114, 228, 0.08)" : undefined }}
                        >
                          <td>
                            <div className="font-weight-bold">{displayName(item)}</div>
                            <small className="text-muted d-block">
                              {item.kind === "business" ? CATEGORY_LABEL[item.category] || item.category : item.businessName}
                            </small>
                          </td>
                          <td>{provinceName(item.provinceId)}</td>
                          <td>{item.contactName || "—"}</td>
                          <td>
                            <Badge color={STATUS_COLOR[item.status]} pill>
                              {STATUS_LABEL[item.status]}
                            </Badge>
                          </td>
                          <td>{formatDateTime(item.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </AdminStateBlock>
              </CardBody>
            </Card>
          </Col>

          <Col xl="5" className="mb-4">
            <Card className="shadow">
              <CardHeader>
                <h3 className="mb-0">Başvuru onayı</h3>
                <small className="text-muted">Seçilen başvuruyu inceleyip onaylayın veya reddedin.</small>
              </CardHeader>
              <CardBody>
                {!selected ? (
                  <p className="text-muted mb-0">İncelemek için soldan bir başvuru seçin.</p>
                ) : (
                  <>
                    <h4 className="mb-1">{displayName(selected)}</h4>
                    <Badge color={STATUS_COLOR[selected.status]} pill className="mb-3">
                      {STATUS_LABEL[selected.status]}
                    </Badge>
                    <dl className="mb-3">
                      {selected.kind === "deal" ? (
                        <>
                          <DetailRow label="İşletme">{selected.businessName}</DetailRow>
                          <DetailRow label="Şehir · Tema">
                            {[provinceName(selected.provinceId), selected.theme].filter(Boolean).join(" · ")}
                          </DetailRow>
                          <DetailRow label="Fiyat">{selected.priceText}</DetailRow>
                          <DetailRow label="Tarihler">
                            {`${formatDay(selected.startDate)} – ${formatDay(selected.endDate)}`}
                          </DetailRow>
                          <DetailRow label="Buton metni">{selected.contactCta}</DetailRow>
                        </>
                      ) : selected.kind === "ad" ? (
                        <>
                          <DetailRow label="İşletme">{selected.businessName}</DetailRow>
                          <DetailRow label="Alt başlık">{selected.subtitle}</DetailRow>
                          <DetailRow label="Şehir">{provinceName(selected.provinceId)}</DetailRow>
                          <DetailRow label="Yerleşim">{PLACEMENT_LABEL[selected.placement] || selected.placement}</DetailRow>
                          <DetailRow label="Görsel">
                            {safeHttpUrl(selected.imageUrl) ? (
                              <a href={safeHttpUrl(selected.imageUrl)} target="_blank" rel="noopener noreferrer">
                                <img
                                  src={safeHttpUrl(selected.imageUrl)}
                                  alt={selected.title}
                                  style={{ maxWidth: "100%", maxHeight: 160, borderRadius: 8, objectFit: "cover" }}
                                />
                              </a>
                            ) : null}
                          </DetailRow>
                          <DetailRow label="Bağlantı">
                            {safeHttpUrl(selected.linkUrl) ? (
                              <a href={safeHttpUrl(selected.linkUrl)} target="_blank" rel="noopener noreferrer">
                                {selected.linkUrl}
                              </a>
                            ) : null}
                          </DetailRow>
                        </>
                      ) : (
                        <>
                          <DetailRow label="Kategori">{CATEGORY_LABEL[selected.category] || selected.category}</DetailRow>
                          <DetailRow label="Konum">
                            {[selected.district, provinceName(selected.provinceId)].filter(Boolean).join(", ")}
                          </DetailRow>
                          <DetailRow label="Adres">{selected.address}</DetailRow>
                          <DetailRow label="Web sitesi">{selected.website}</DetailRow>
                          <DetailRow label="Vergi no">{selected.taxNumber}</DetailRow>
                        </>
                      )}
                      <DetailRow label="Açıklama">
                        {selected.description ? <span style={{ whiteSpace: "pre-wrap" }}>{selected.description}</span> : null}
                      </DetailRow>
                      <DetailRow label="Yetkili">{selected.contactName}</DetailRow>
                      <DetailRow label="Telefon">
                        {selected.phone ? <a href={`tel:${selected.phone.replace(/\s/g, "")}`}>{selected.phone}</a> : null}
                      </DetailRow>
                      <DetailRow label="E-posta">
                        {selected.email ? <a href={`mailto:${selected.email}`}>{selected.email}</a> : null}
                      </DetailRow>
                      <DetailRow label="Başvuru tarihi">{formatDateTime(selected.createdAt)}</DetailRow>
                      {selected.reviewedAt ? <DetailRow label="İnceleme">{formatDateTime(selected.reviewedAt)}</DetailRow> : null}
                      {selected.adminNote ? <DetailRow label="Not">{selected.adminNote}</DetailRow> : null}
                    </dl>
                    {selected.status === "pending" ? (
                      <>
                        <FormGroup>
                          <Label>İnceleme notu (isteğe bağlı)</Label>
                          <Input
                            type="textarea"
                            rows={3}
                            value={note}
                            onChange={(event) => setNote(event.target.value)}
                            placeholder="Örn. Vergi numarası doğrulanamadı"
                          />
                        </FormGroup>
                        <Button color="success" className="mr-2" disabled={Boolean(busyId)} onClick={() => review(selected, "approved")}>
                          {busyId === selected.id ? "İşleniyor…" : APPROVE_LABEL[selected.kind]}
                        </Button>
                        <Button color="danger" outline disabled={Boolean(busyId)} onClick={() => review(selected, "rejected")}>
                          Reddet
                        </Button>
                      </>
                    ) : (
                      <p className="text-muted mb-0">Bu başvuru zaten işlenmiş.</p>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
